import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaChartLine, FaUser, FaClock, FaComments } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { useStudentAuth } from '../../../lib/AuthProvider';
import { getSupabase } from '../../../lib/supabaseClient';
import './StudentAttendance.css';

interface AttendanceForm {
  date: string;
  location: string;
  period: string;
  checkType: string;
  time: string;
}

const StudentAttendance: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { student, loading: authLoading, signOut } = useStudentAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState<AttendanceForm>({
    date: '',
    location: '',
    period: '',
    checkType: '',
    time: ''
  });
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [locations, setLocations] = useState<Array<{id: string, name: string, latitude: number, longitude: number}>>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Check session validity on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const supabase = getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.log('Session invalid in StudentAttendance, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
        
        // If session exists but no student data and not loading, wait a bit more
        if (!student && !authLoading) {
          console.log('No student data but session exists, waiting for AuthProvider to load...');
          setTimeout(() => {
            if (!student && !authLoading) {
              console.log('Still no student data after timeout, redirecting to login');
              navigate('/', { replace: true });
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session in StudentAttendance:', error);
      }
    };

    if (!authLoading) {
      checkSession();
    }
  }, [student, authLoading, navigate]);

  // Fetch locations for current student when component mounts or student changes
  useEffect(() => {
    if (student?.id) {
      fetchLocations(student.id);
    }
  }, [student?.id]);

  // Fetch locations for current student (by user_id) from the locations table
  const fetchLocations = async (studentUserId: string) => {
    setLocationsLoading(true);
    try {
      const supabase = getSupabase();
      const { data: locationsData, error } = await supabase
        .from('locations')
        .select('id, name, latitude, longitude')
        .eq('user_id', studentUserId)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching locations:', error);
        return;
      }

      setLocations(locationsData || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLocationsLoading(false);
    }
  };

  // Handle attendance form submission
  const handleAttendanceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!student) {
      alert('Student information not available. Please try again.');
      return;
    }

    // Validate required fields
    if (!attendanceForm.date || !attendanceForm.location || !attendanceForm.period || !attendanceForm.checkType || !attendanceForm.time) {
      alert('Please fill in all required fields.');
      return;
    }

    setAttendanceLoading(true);
    try {
      const supabase = getSupabase();

      // Get selected location details
      const selectedLocation = locations.find(loc => loc.id === attendanceForm.location);
      if (!selectedLocation) {
        alert('Selected location not found.');
        return;
      }

      // Type for supabase insert operation
      type SupabaseInsertOperation = {
        from: (table: string) => {
          insert: (data: Record<string, any> | Record<string, any>[]) => Promise<{error: any}>;
        };
      };

      const supabaseTyped = supabase as unknown as SupabaseInsertOperation;

      // Helper function to create UTC datetime from time input
      const convertToUTC = (time: string, date: string) => {
        const localDateTime = new Date(`${date}T${time}:00`);
        return localDateTime.toISOString();
      };

      // Determine check_type based on period and checkType
      const checkTypeMap: Record<string, string> = {
        'am_time_in': 'check_in_am',
        'am_time_out': 'check_out_am',
        'pm_time_in': 'check_in_pm',
        'pm_time_out': 'check_out_pm'
      };
      const checkTypeKey = `${attendanceForm.period}_${attendanceForm.checkType}`;
      const checkType = checkTypeMap[checkTypeKey];

      if (!checkType) {
        alert('Invalid period or check type combination.');
        return;
      }

      // Create single check-in record
      const timeUTC = convertToUTC(attendanceForm.time, attendanceForm.date);
      const checkInRecord = {
        user_id: student.id,
        student_id: student.student_id,
        first_name: student.first_name,
        last_name: student.last_name,
        location_id: attendanceForm.location,
        check_type: checkType,
        check_time: timeUTC,
        check_date: attendanceForm.date,
        is_within_radius: true,
        coordinates: { 
          lat: selectedLocation.latitude, 
          lng: selectedLocation.longitude 
        }
      };

      // Insert check-in
      const { error } = await supabaseTyped
        .from('check_ins')
        .insert(checkInRecord);

      if (error) {
        console.error('Error requesting attendance:', error);
        alert('Error requesting attendance. Please try again.');
        return;
      }

      // Reset form
      setAttendanceForm({
        date: '',
        location: '',
        period: '',
        checkType: '',
        time: ''
      });

      alert('Attendance requested successfully!');
    } catch (error) {
      console.error('Error requesting attendance:', error);
      alert('Error requesting attendance. Please try again.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Handle form input changes
  const handleAttendanceFormChange = (field: keyof AttendanceForm, value: string) => {
    setAttendanceForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="student-home">
      <header className="top-nav">
        <div className="logo">
          <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
        </div>
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          aria-controls="mobile-sidebar"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(true)}
        >
          {'\u2630'}
        </button>
        <div className="nav-links">
          <button 
            onClick={() => navigate('/student/home')} 
            className={`nav-link ${isActive('/student/home') ? 'active' : ''}`}
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className={`nav-link ${isActive('/student/attendance') ? 'active' : ''}`}
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className={`nav-link ${isActive('/student/progress') ? 'active' : ''}`}
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className={`nav-link ${isActive('/student/feedback') ? 'active' : ''}`}
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className={`nav-link ${isActive('/student/profile') ? 'active' : ''}`}
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="mobile-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <button 
            onClick={() => navigate('/student/home')} 
            className="nav-link"
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className="nav-link"
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className="nav-link"
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </nav>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1>Attendance</h1>
        </div>

        {authLoading ? (
          <div className="attendance-loading">
            <p>Loading...</p>
          </div>
        ) : student ? (
          <div className="attendance-section">

            <div className="attendance-form-container">
              <form onSubmit={handleAttendanceSubmit} className="attendance-form">
                <div className="form-group">
                  <label htmlFor="date">Date *</label>
                  <input
                    type="date"
                    id="date"
                    value={attendanceForm.date}
                    onChange={(e) => handleAttendanceFormChange('date', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location *</label>
                  <select
                    id="location"
                    value={attendanceForm.location}
                    onChange={(e) => handleAttendanceFormChange('location', e.target.value)}
                    className="form-select"
                    disabled={locationsLoading}
                    required
                  >
                    <option value="">
                      {locationsLoading 
                        ? "Loading locations..." 
                        : "Select Location"
                      }
                    </option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="period">Period *</label>
                  <select
                    id="period"
                    value={attendanceForm.period}
                    onChange={(e) => handleAttendanceFormChange('period', e.target.value)}
                    className="form-select"
                    required
                  >
                    <option value="">Select Period</option>
                    <option value="am">AM</option>
                    <option value="pm">PM</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="checkType">Time Type *</label>
                  <select
                    id="checkType"
                    value={attendanceForm.checkType}
                    onChange={(e) => handleAttendanceFormChange('checkType', e.target.value)}
                    className="form-select"
                    required
                  >
                    <option value="">Select Time Type</option>
                    <option value="time_in">Time In</option>
                    <option value="time_out">Time Out</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="time">Time *</label>
                  <input
                    type="time"
                    id="time"
                    value={attendanceForm.time}
                    onChange={(e) => handleAttendanceFormChange('time', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={attendanceLoading}
                    className="submit-button"
                  >
                    {attendanceLoading ? 'Requesting...' : 'Request Attendance'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="attendance-error">
            <p>Unable to load attendance form. Please try again later.</p>
            <button 
              className="action-button" 
              onClick={() => navigate('/student/home')}
            >
              Return to Home
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentAttendance;
